// PWA utilities for offline functionality and mobile app features

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
};

let deferredInstallPrompt: BeforeInstallPromptEvent | null = null;
if (typeof window !== "undefined") {
  window.addEventListener("appinstalled", () => {
    localStorage.setItem("is_pwa_installed", "true");
  });

  // If we ever detect standalone mode, remember it so we don't prompt in browser tabs later
  if (
    (window.navigator as any).standalone ||
    window.matchMedia("(display-mode: standalone)").matches
  ) {
    localStorage.setItem("is_pwa_installed", "true");
  }
}

// Check if the app is running as a PWA
export const isPWA = () => {
  return window.matchMedia("(display-mode: standalone)").matches;
};

// Check if the app is installed
export const isInstalled = () => {
  if (typeof window === "undefined") return false;
  return (
    (window.navigator as any).standalone ||
    window.matchMedia("(display-mode: standalone)").matches ||
    localStorage.getItem("is_pwa_installed") === "true"
  );
};

// Request notification permission
export const requestNotificationPermission = async () => {
  if (!("Notification" in window)) {
    return false;
  }

  if (Notification.permission === "granted") {
    return true;
  }

  if (Notification.permission !== "denied") {
    const permission = await Notification.requestPermission();
    return permission === "granted";
  }

  return false;
};

// Show notification
export const showNotification = (title: string, options?: any) => {
  if (!("Notification" in window) || Notification.permission !== "granted") {
    return;
  }

  const notification = new Notification(title, {
    icon: "/images/logo.png",
    badge: "/images/logo.png",
    vibrate: [100, 50, 100],
    ...options,
  });

  return notification;
};

// Register service worker
export const registerServiceWorker = async () => {
  if ("serviceWorker" in navigator) {
    try {
      const registration = await navigator.serviceWorker.register("/sw.js", {
        scope: "/",
      });
      console.log("Service Worker registered:", registration);
      return registration;
    } catch (error) {
      console.error("Service Worker registration failed:", error);
    }
  }
  return null;
};

// Check for service worker updates
export const checkForUpdates = async () => {
  if (!("serviceWorker" in navigator)) {
    return false;
  }

  const registration = await navigator.serviceWorker.ready;

  try {
    await registration.update();
    return true;
  } catch (error) {
    console.log("No update available");
    return false;
  }
};

// Add to home screen
export const addToHomeScreen = async () => {
  if (!deferredInstallPrompt) {
    return false;
  }

  await deferredInstallPrompt.prompt();

  const { outcome } = await deferredInstallPrompt.userChoice;

  if (outcome !== "accepted") {
    return false;
  }

  deferredInstallPrompt = null;

  return true;
};

export const setInstallPromptEvent = (event: Event | null) => {
  deferredInstallPrompt = event as BeforeInstallPromptEvent | null;
};

export const getInstallPromptEvent = () => {
  return deferredInstallPrompt;
};

export const clearInstallPromptEvent = () => {
  deferredInstallPrompt = null;
};

// Check if can add to home screen
export const canAddToHomeScreen = () => {
  return !!deferredInstallPrompt;
};

// Share functionality
export const shareContent = async (
  title: string,
  text: string,
  url?: string,
) => {
  if (navigator.share) {
    try {
      await navigator.share({
        title,
        text,
        url: url || window.location.href,
      });
      return true;
    } catch (error) {
      console.log("Share failed:", error);
      return false;
    }
  }

  // Fallback: copy to clipboard
  try {
    await navigator.clipboard.writeText(
      `${title}\n${text}\n${url || window.location.href}`,
    );
    return true;
  } catch (error) {
    console.log("Clipboard write failed:", error);
    return false;
  }
};

// Get device info
export const getDeviceInfo = () => {
  const userAgent = navigator.userAgent;

  const isMobile =
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      userAgent,
    );
  const isTablet =
    /iPad|Android(?!.*Mobile)|Silk/i.test(userAgent) && !isMobile;
  const isDesktop = !isMobile && !isTablet;

  return {
    isMobile,
    isTablet,
    isDesktop,
    userAgent,
    platform: navigator.platform,
    language: navigator.language,
  };
};

// Get connection info
export const getConnectionInfo = () => {
  const connection =
    (navigator as any).connection ||
    (navigator as any).mozConnection ||
    (navigator as any).webkitConnection;

  if (!connection) {
    return null;
  }

  return {
    effectiveType: connection.effectiveType,
    downlink: connection.downlink,
    downlinkMax: connection.downlinkMax,
    rtt: connection.rtt,
    saveData: connection.saveData,
    online: navigator.onLine,
  };
};

// Vibration API
export const vibrate = (pattern: number | number[]) => {
  if ("vibrate" in navigator) {
    return navigator.vibrate(pattern);
  }
  return false;
};

// Screen orientation
export const getScreenOrientation = () => {
  return window.screen.orientation?.type ||
    window.matchMedia("(orientation: portrait)").matches
    ? "portrait"
    : "landscape";
};

// Fullscreen API
export const toggleFullscreen = async () => {
  if (!document.fullscreenElement) {
    try {
      await document.documentElement.requestFullscreen();
    } catch (error) {
      console.log("Fullscreen request failed:", error);
    }
  } else {
    try {
      await document.exitFullscreen();
    } catch (error) {
      console.log("Exit fullscreen failed:", error);
    }
  }
};

export const isFullscreen = () => {
  return !!document.fullscreenElement;
};

// App visibility
export const isPageVisible = () => {
  return !document.hidden;
};

// Performance monitoring
export const getPerformanceMetrics = () => {
  if ("performance" in window) {
    const navigation = performance.getEntriesByType(
      "navigation",
    )[0] as PerformanceNavigationTiming;
    const paint = performance.getEntriesByType("paint");

    return {
      // Navigation timing
      domContentLoaded:
        navigation?.domContentLoadedEventEnd -
        navigation?.domContentLoadedEventStart,
      loadComplete: navigation?.loadEventEnd - navigation?.loadEventStart,
      firstPaint: paint.find((p) => p.name === "first-paint")?.startTime,
      firstContentfulPaint: paint.find(
        (p) => p.name === "first-contentful-paint",
      )?.startTime,

      // Resource timing
      resources: performance.getEntriesByType("resource").map((resource) => ({
        name: resource.name,
        duration: resource.duration,
        size: (resource as any).transferSize || 0,
        type: (resource as any).initiatorType,
      })),
    };
  }

  return null;
};
